/**
 * useMedia.js
 *
 * React Query hooks for media file operations.
 *
 * Usage:
 *   const { data: mediaFiles = [], isLoading } = useMedia(userId);
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaService } from 'services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────────────────
export const mediaKeys = {
  all: (userId) => ['media', userId],
  storage: (userId) => ['media', 'storage', userId],
};

// ─────────────────────────────────────────────────────────────────────────────
// QUERIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch all media for a user.
 * Replaces: axios.get(`${api}media/${userId}`) in Media/index.js, MediModel.js, etc.
 *
 * @param {string|number} userId
 */
export const useMedia = (userId, options = {}) => {
  return useQuery({
    queryKey: mediaKeys.all(userId),
    queryFn: () => mediaService.getMedia(userId),
    select: (data) => data?.media ?? [],
    enabled: !!userId,
    ...options,
  });
};

/**
 * Fetch storage usage stats.
 * Replaces: axios.get(`${api}user/${userId}/storage`) in UserStorageCard.js
 *
 * @param {string|number} userId
 */
export const useStorageStats = (userId, options = {}) => {
  return useQuery({
    queryKey: mediaKeys.storage(userId),
    queryFn: () => mediaService.getStorageStats(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// MUTATIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upload media to server — auto-invalidates media list.
 */
export const useUploadMedia = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) => mediaService.uploadToServer(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.storage(userId) });
    },
  });
};

/**
 * Soft delete a media file.
 */
export const useDeleteMedia = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => mediaService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.storage(userId) });
    },
  });
};

/**
 * Permanently delete a media file.
 */
export const usePermanentDeleteMedia = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => mediaService.permanentDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.storage(userId) });
    },
  });
};
